/**
 * "Customer service" tenant preset for the all-tenant view.
 *
 * These are the tenants where kolula handles (end-)customer service. The
 * eventual source of truth is a `covers_customer_service` flag on the
 * operational `tenant` table (handoff spec:
 * `docs/plans/all-tenant-view-customer-service-flag.md`). The tenants
 * shown in the all-tenant view today are all *legacy* tenants that have
 * no row in the operational DB, so the flag cannot drive this yet — this
 * module is the documented workaround until the backend lands.
 *
 * The set is an explicit **allow-list**, not a deny-list: customer service
 * is a paid service, so a tenant is *not* part of the preset until it is
 * added here on purpose (i.e. has contracted for it). This mirrors the
 * planned `covers_customer_service` column (`@default(false)` — opt-in),
 * so the workaround and the eventual flag agree on semantics. Curate this
 * list as customer-service coverage changes.
 */
export const CUSTOMER_SERVICE_TENANT_SLUGS: ReadonlySet<string> = new Set<string>([
    'kolula',
    'vonovia_v2',
    'dhu',
    'seezeit',
    'strandbad-pertisau',
    'badesee-petershagen',
    'kolula-key',
]);

/** Whether kolula covers (end-)customer service for the given tenant. */
export const isCustomerServiceTenant = (slug: string): boolean =>
    CUSTOMER_SERVICE_TENANT_SLUGS.has(slug);

/** Filters a list of accessible tenant slugs down to the serviced ones. */
export const getCustomerServiceTenants = (accessibleTenants: readonly string[]): string[] =>
    accessibleTenants.filter(isCustomerServiceTenant);
